import { useContext, useState, useCallback, useEffect } from "react"
import { List, Eye, EyeOff, Plus, Trash } from "lucide-react"
import { Reorder } from "framer-motion"

import styles from "./pageReorder.module.css"
import { TemplateContext } from "../../../../../../../../contexts/TemplateContext"
import { AppContext } from "../../../../../../../../contexts/AppContext"
import { defaultPageOrder, type Page } from "../../../../../../../../sdkStructs"

export default function PageReorder() {
  const { template, setTemplate } = useContext(TemplateContext)
  const { pushAlert } = useContext(AppContext);
  const [localPageOrder, setLocalPageOrder] = useState<Page[]>(template?.PageOrder || defaultPageOrder)
  const [editingPage, setEditingPage] = useState<string | null>(null)
  const [newPageName, setNewPageName] = useState<string>("")

  const handleReorder = useCallback((newOrder: Page[]) => {
    setLocalPageOrder(newOrder)
  }, [])

  useEffect(() => {
    setLocalPageOrder(template?.PageOrder || defaultPageOrder)
  }, [template?.PageOrder])

  const handleDragEnd = useCallback(() => {
    const updatedOrder = localPageOrder.map((page, index) => ({
      ...page,
      Position: index + 1,
    }))
    setTemplate((prevTemplate) => {
      if (!prevTemplate) return prevTemplate;
      return {
        ...prevTemplate,
        PageOrder: updatedOrder,
      }
    })
  }, [localPageOrder, setTemplate])

  const toggleDisabled = useCallback(
    (name: string) => {
      const newOrder = localPageOrder.map((item) => (item.Name === name ? { ...item, Hidden: !item.Hidden } : item))
      setTemplate((prevTemplate) => {
        if (!prevTemplate) return prevTemplate;
        return {
          ...prevTemplate,
          PageOrder: newOrder,
        }
      })
    },
    [localPageOrder, setTemplate],
  )

  const addPage = useCallback(() => {
    if (!template) return;
    
    const newPageName = `Page ${template.PageOrder.length + 1}`
    
    if (template.PageOrder.some(page => page.Name === newPageName)) {
      pushAlert({
        title: "Error",
        text: "A page with this name already exists",
        type: "error",
        closeButton: true,
        time: 5000
      });
      return;
    }
    
    const newPage: Page = { Name: newPageName, Position: template.PageOrder.length + 1, Hidden: false, Section: "" }
    const newOrder = [...template.PageOrder, newPage]
    
    setTemplate((prevTemplate) => {
      if (!prevTemplate) return prevTemplate;
      return {
        ...prevTemplate,
        PageOrder: newOrder,
      }
    })
  }, [template, setTemplate, pushAlert])

  const deletePage = useCallback(
    (name: string) => {
      const newOrder = localPageOrder.filter((item) => item.Name !== name)
      setTemplate((prevTemplate) => {
        if (!prevTemplate) return prevTemplate;
        return {
          ...prevTemplate,
          PageOrder: newOrder,
        }
      })
    },
    [localPageOrder, setTemplate],
  )

  const startEditing = useCallback((name: string) => {
    setEditingPage(name)
    setNewPageName(name)
    setTimeout(() => {
      const input = document.querySelector(`.${styles.renameInput}`) as HTMLInputElement
      input?.focus()
    }, 0)
  }, [])

  const handleRename = useCallback(() => {
    // Numele nu s-a schimbat
    if (editingPage === newPageName) {
      setEditingPage(null);
      return;
    }
    
    if (localPageOrder.some(page => page.Name === newPageName && page.Name !== editingPage)) {
      pushAlert({
        title: "Error",
        text: "A page with this name already exists",
        type: "error",
        closeButton: true,
        time: 5000
      });

      setEditingPage(null)
      return;
    }
    
    const newOrder = localPageOrder.map((item) => (item.Name === editingPage ? { ...item, Name: newPageName } : item))
    setLocalPageOrder(newOrder)
    setTemplate((prevTemplate) => {
      if (!prevTemplate) return prevTemplate;
      return {
        ...prevTemplate,
        PageOrder: newOrder,
      }
    })
  
    setEditingPage(null)
    setNewPageName("")
  }, [editingPage, newPageName, localPageOrder, setTemplate, pushAlert])

  const isDefaultPage = useCallback((name: string) => {
    return defaultPageOrder.some((page: Page) => page.Name === name)
  }, [])

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <List className={styles.icon} />
        <h1>Page Order</h1>
      </div>

      <Reorder.Group axis="y" values={localPageOrder} onReorder={handleReorder} className={styles.list}>
        {localPageOrder.map((item) => (
          <Reorder.Item
            key={item.Name}
            value={item}
            className={`${styles.listItem} ${item.Hidden ? styles.disabled : ""}`}
            whileDrag={{
              scale: 1.02,
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            }}
            onDragEnd={handleDragEnd}
          >
            <div className={styles.side}>
              <span className={styles.dragHandle}>⋮⋮</span>
              {editingPage === item.Name ? (
                <input
                  type="text"
                  value={newPageName}
                  onChange={(e) => setNewPageName(e.target.value)}
                  onBlur={handleRename}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleRename()
                    }
                  }}
                  className={styles.renameInput}
                />
              ) : (
                <span
                  className={`${styles.itemContent} ${!isDefaultPage(item.Name) ? styles.editable : ""}`}
                  onClick={() => {
                    if (!isDefaultPage(item.Name)) {
                      startEditing(item.Name)
                    }
                  }}
                >
                  {item.Name}
                </span>
              )}
            </div>
            <div className={styles.side}>
              {!isDefaultPage(item.Name) && (
                <button
                  className={styles.deleteButton}
                  onClick={(e) => {
                    e.preventDefault()
                    deletePage(item.Name)
                  }}
                >
                  <Trash size={16} />
                </button>
              )}
              <button
                className={styles.eyeButton}
                onClick={(e) => {
                  e.preventDefault()
                  toggleDisabled(item.Name)
                }}
              >
                {item.Hidden ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </Reorder.Item>
        ))}
      </Reorder.Group>
      <button className={styles.addButton} onClick={addPage}>
        <Plus size={16} className={styles.plusIcon} />
        <span className={styles.noMargin}>Add Page</span>
      </button>
    </div>
  )
}